import { Check, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button.js';
import { Combobox } from '../../../components/ui/combobox.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog.js';
import { Label } from '../../../components/ui/label.js';
import {
  useScenarioEventSubscriptions,
  useScenariosList,
  type ScenarioEventSubscription,
} from '../hooks.js';
import { asScenarioRow, type ScenarioRow } from '../types.js';

/**
 * Compare event subscriptions between two scenarios. Mirrors the
 * legacy `wf-audit/src/slices/scenario/components/scenario/
 * CompareEventSubscriprion.tsx` rendering shape:
 *
 *   • Two scenario combobox pickers (no team selector — the new
 *     SPA's list is already license-scoped).
 *   • `Compare` CTA fetches the subscriptions for each side via the
 *     engine's `GET /v1/scenarios/{id}/event-subscriptions` proxy.
 *   • Side-by-side panes — each subscription rendered as
 *     `Event type / Object code / Filters` with a check (matched in
 *     the other pane) or X (no match) badge.
 *
 * Diff rule (legacy parity): two subscriptions are considered equal
 * when their `objCode + eventType + sorted-filter-fingerprint` line
 * up.
 */
export interface ScenarioCompareEventSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  defaultFusionId?: string;
}

export function ScenarioCompareEventSubscriptionDialog({
  open,
  onOpenChange,
  defaultFusionId,
}: ScenarioCompareEventSubscriptionDialogProps): JSX.Element {
  const { data: rawScenarios = [], isLoading: scenariosLoading } = useScenariosList();
  const scenarios = useMemo<ReadonlyArray<ScenarioRow>>(
    () => rawScenarios.map(asScenarioRow).filter((r) => r.fusionId.length > 0),
    [rawScenarios],
  );

  const [leftScenarioId, setLeftScenarioId] = useState('');
  const [rightScenarioId, setRightScenarioId] = useState('');
  const [comparing, setComparing] = useState(false);
  // One-shot seed flag — without this the seeding effect would re-run
  // on every leftScenarioId change and snap the picker back to the
  // default, blocking the operator from picking anything else.
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (!open) {
      setSeeded(false);
      return;
    }
    if (seeded || scenarios.length === 0) return;
    if (defaultFusionId && scenarios.some((s) => s.fusionId === defaultFusionId)) {
      setLeftScenarioId(defaultFusionId);
      setRightScenarioId(defaultFusionId);
    } else {
      const seed = scenarios[0]!.fusionId;
      setLeftScenarioId(seed);
      setRightScenarioId(seed);
    }
    setSeeded(true);
  }, [open, defaultFusionId, scenarios, seeded]);

  // Reset the "comparing" gate when inputs change so the fetch only
  // happens after an explicit Compare click. Same pattern as the
  // blueprint dialog.
  useEffect(() => {
    setComparing(false);
  }, [leftScenarioId, rightScenarioId]);

  const leftFetch = useScenarioEventSubscriptions(
    comparing ? leftScenarioId || undefined : undefined,
    comparing,
  );
  const rightFetch = useScenarioEventSubscriptions(
    comparing ? rightScenarioId || undefined : undefined,
    comparing,
  );
  const isLoading = comparing && (leftFetch.isFetching || rightFetch.isFetching);

  const scenarioOptions = useMemo(
    () =>
      [...scenarios]
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
        .map((s) => ({ value: s.fusionId, label: s.name || s.fusionId })),
    [scenarios],
  );

  const canCompare = !!leftScenarioId && !!rightScenarioId && !isLoading;

  const leftSubs = leftFetch.data?.subscriptions ?? [];
  const rightSubs = rightFetch.data?.subscriptions ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-[92vw] flex-col overflow-hidden border-foreground/15 bg-[color:var(--surface-overlay)] text-foreground xl:max-w-[80vw]">
        <DialogHeader>
          <DialogTitle>Compare event subscriptions</DialogTitle>
          <DialogDescription className="text-foreground/60">
            Diff the Workfront event subscriptions behind each scenario&rsquo;s webhook.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 xl:flex-row xl:items-end">
          <ScenarioPicker
            sideLabel="Left"
            scenarioId={leftScenarioId}
            onScenarioChange={setLeftScenarioId}
            scenarioOptions={scenarioOptions}
            scenariosLoading={scenariosLoading}
          />
          <div className="flex shrink-0 items-center justify-center xl:pb-1">
            <Button
              type="button"
              onClick={() => setComparing(true)}
              disabled={!canCompare}
              className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
            >
              {isLoading ? 'Comparing…' : 'Compare'}
            </Button>
          </div>
          <ScenarioPicker
            sideLabel="Right"
            scenarioId={rightScenarioId}
            onScenarioChange={setRightScenarioId}
            scenarioOptions={scenarioOptions}
            scenariosLoading={scenariosLoading}
          />
        </div>

        <div className="mt-2 grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-hidden md:grid-cols-2">
          <SubscriptionPane
            comparing={comparing}
            fetch={leftFetch}
            mySubs={leftSubs}
            otherSubs={rightSubs}
          />
          <SubscriptionPane
            comparing={comparing}
            fetch={rightFetch}
            mySubs={rightSubs}
            otherSubs={leftSubs}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScenarioPicker({
  sideLabel,
  scenarioId,
  onScenarioChange,
  scenarioOptions,
  scenariosLoading,
}: {
  sideLabel: 'Left' | 'Right';
  scenarioId: string;
  onScenarioChange: (next: string) => void;
  scenarioOptions: ReadonlyArray<{ value: string; label: string }>;
  scenariosLoading: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-1 flex-col gap-2 rounded-md border border-foreground/10 bg-foreground/[0.02] p-3">
      <span className="text-xs uppercase tracking-wide text-foreground/40">{sideLabel}</span>
      <Label className="text-xs text-foreground/70">Scenario</Label>
      <Combobox
        value={scenarioId}
        onValueChange={onScenarioChange}
        options={[...scenarioOptions]}
        placeholder={scenariosLoading ? 'Loading…' : 'Pick scenario'}
        searchPlaceholder="Search scenarios…"
        emptyText="No scenarios."
      />
    </div>
  );
}

function SubscriptionPane({
  comparing,
  fetch,
  mySubs,
  otherSubs,
}: {
  comparing: boolean;
  fetch: ReturnType<typeof useScenarioEventSubscriptions>;
  mySubs: ReadonlyArray<ScenarioEventSubscription>;
  otherSubs: ReadonlyArray<ScenarioEventSubscription>;
}): JSX.Element {
  const otherKeys = useMemo(
    () => new Set(mySubs.length > 0 ? otherSubs.map(fingerprint) : []),
    [otherSubs, mySubs.length],
  );

  if (!comparing) {
    return (
      <PaneEmpty>
        Pick scenarios on both sides and hit Compare to see their subscriptions.
      </PaneEmpty>
    );
  }
  if (fetch.isFetching) {
    return <PaneEmpty>Loading subscriptions…</PaneEmpty>;
  }
  if (fetch.error) {
    return (
      <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-800 dark:text-rose-200">
        {fetch.error.message}
      </div>
    );
  }
  if (mySubs.length === 0) {
    return <PaneEmpty>No event subscriptions for this scenario.</PaneEmpty>;
  }
  const sorted = [...mySubs].sort((a, b) => fingerprint(a).localeCompare(fingerprint(b)));
  return (
    <div className="overflow-y-auto rounded-md border border-foreground/10 bg-foreground/[0.02] p-3">
      {sorted.map((sub, i) => {
        const matched = otherKeys.has(fingerprint(sub));
        return (
          <article
            key={sub.id ?? `${fingerprint(sub)}-${i}`}
            className="relative border-b border-foreground/5 py-3 last:border-b-0"
          >
            <p className="text-sm">
              <span className="text-foreground/60">Event type: </span>
              <span className="text-[color:var(--brand-yellow)]">{sub.eventType ?? '—'}</span>
            </p>
            <p className="text-sm">
              <span className="text-foreground/60">Object code: </span>
              <span className="text-[color:var(--brand-yellow)]">{sub.objCode ?? '—'}</span>
            </p>
            {sub.filters?.some((f) => f.fieldName === 'lastUpdatedByID') ? (
              <p className="text-xs italic text-foreground/70">Connection user updates excluded</p>
            ) : null}
            {sub.filters && sub.filters.length > 0 ? (
              <div className="mt-1.5">
                <span className="text-xs uppercase tracking-wide text-foreground/40">Filters</span>
                <ul className="mt-1 space-y-0.5 text-sm">
                  {sub.filters
                    .slice()
                    .sort((a, b) => (a.fieldName ?? '').localeCompare(b.fieldName ?? ''))
                    .map((f, idx) => (
                      <li key={idx}>
                        {f.fieldName} {comparisonLabel(f.comparison)}{' '}
                        <span className="text-[color:var(--brand-yellow)]">
                          {typeof f.fieldValue === 'object'
                            ? JSON.stringify(f.fieldValue)
                            : String(f.fieldValue ?? '')}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            ) : null}
            <div className="absolute right-0 top-3">
              {matched ? (
                <Check className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <X className="h-6 w-6 text-rose-600 dark:text-rose-400" />
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function PaneEmpty({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex h-full items-center justify-center rounded-md border border-dashed border-foreground/10 p-6 text-center text-sm text-foreground/40">
      {children}
    </div>
  );
}

function comparisonLabel(c: string | undefined): string {
  if (c === 'eq') return 'EQUAL';
  if (c === 'ne') return 'NOT EQUAL';
  return c ?? '';
}

function fingerprint(sub: ScenarioEventSubscription): string {
  const filters = (sub.filters ?? [])
    .map((f) => ({
      fieldName: f.fieldName ?? '',
      fieldValue: f.fieldValue == null ? '' : String(f.fieldValue),
      state: f.state ?? '',
      comparison: f.comparison ?? '',
    }))
    .sort((a, b) => {
      const nameCmp = a.fieldName.localeCompare(b.fieldName);
      if (nameCmp !== 0) return nameCmp;
      return a.fieldValue.localeCompare(b.fieldValue);
    });
  return `${sub.objCode ?? ''}|${sub.eventType ?? ''}|${JSON.stringify(filters)}`;
}
